import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { Calculator, DollarSign, CreditCard, TrendingUp, Percent, ShieldCheck, CheckCircle2, XCircle, Trash2 } from "lucide-react";

// Helpers de exibicao do contrato vinculado (mesmo formato dos cards de baixo)
const fmtMoedaSim = (value) => {
  const v = Number(value) || 0;
  return `R$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtDataSim = (dateStr) => {
  if (!dateStr) return "-";
  const s = String(dateStr).trim();
  if (!s || s === "-") return "-";
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  m = s.match(/^(\d{4})-(\d{1,2})$/);
  if (m) return `${m[2].padStart(2, "0")}/${m[1]}`;
  m = s.match(/^(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[1].padStart(2, "0")}/${m[2]}`;
  m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return s;
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.toLocaleDateString("pt-BR");
  return s;
};

// Componente para um card de simulação individual
const SimulacaoCard = ({
  tipo,
  titulo,
  icon: Icon,
  cor,
  simulacao,
  updateSimulacao,
  toggleSimulacao,
  bancos,
  correspondentes,
  tabelasComissao,
  instituicaoSelecionada,
  getBancosParaSimulacao,
  getTabelasParaBanco,
  getCorrespondentesDoBanco,
  analisarCriteriosSimulacao,
  formatValorBR,
  formatValorBROnBlur,
  tipoOperacaoDisplay,
  tipoOperacaoKey,
  contratosVinculados = [],
  onExcluirContrato
}) => {
  const cores = {
    green: {
      border: "border-green-500 bg-green-50/30",
      text: "text-green-700",
      icon: "text-green-600",
      input: "text-green-600",
      tipoOp: "bg-green-50 text-green-700",
      valorLiq: "bg-green-50 font-semibold text-green-700"
    },
    orange: {
      border: "border-orange-500 bg-orange-50/30",
      text: "text-orange-700",
      icon: "text-orange-600",
      input: "text-orange-600",
      tipoOp: "bg-orange-50 text-orange-700",
      valorLiq: "bg-orange-50 font-semibold text-orange-700"
    },
    cyan: {
      border: "border-cyan-500 bg-cyan-50/30",
      text: "text-cyan-700",
      icon: "text-cyan-600",
      input: "text-cyan-600",
      tipoOp: "bg-cyan-50 text-cyan-700",
      valorLiq: "bg-cyan-50 font-semibold text-cyan-700"
    }
  };

  const corAtual = cores[cor] || cores.green;

  return (
    <div className={cn(
      "border rounded-lg p-3 transition-all",
      simulacao.ativo ? cn("border-2 bg-slate-50/80 shadow-sm", corAtual.border) : "border-slate-200 bg-white"
    )}>
      <div className="flex items-center gap-2 mb-2">
        <div className="flex items-center gap-1.5">
          <Icon className={cn("h-4 w-4", corAtual.icon)} />
          <span className={cn("font-semibold text-sm whitespace-nowrap", corAtual.text)}>{titulo}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground">Margem:</span>
          <Input 
            className={cn("h-6 w-20 text-xs font-semibold px-1", corAtual.input)}
            placeholder="0,00"
            value={simulacao.margemDisponivel}
            onChange={(e) => updateSimulacao(tipo, 'margemDisponivel', formatValorBR(e.target.value))}
            onBlur={(e) => updateSimulacao(tipo, 'margemDisponivel', formatValorBROnBlur(e.target.value))}
          />
        </div>
        <Switch 
          checked={simulacao.ativo} 
          onCheckedChange={() => toggleSimulacao(tipo)}
        />
      </div>
      {contratosVinculados && contratosVinculados.length > 0 && (
        <div className="mb-3 space-y-1.5">
          {contratosVinculados.map((ct) => (
            <div key={ct.id} className="rounded-md border border-slate-200 bg-white px-3 py-2 flex items-center gap-2" data-testid={`sim-contrato-${ct.id}`}>
              <div className="grid grid-cols-7 gap-x-2 gap-y-0 text-[11px] flex-1">
                <div className="flex items-baseline gap-1 min-w-0"><span className="text-slate-400 shrink-0">Nº Contrato</span><span className="font-mono font-semibold truncate">{ct.numero_contrato || "-"}</span></div>
                <div className="flex items-baseline gap-1 min-w-0"><span className="text-slate-400 shrink-0">Banco</span><span className="font-medium truncate">{ct.banco_nome || "-"}</span></div>
                <div className="flex items-baseline gap-1 min-w-0"><span className="text-slate-400 shrink-0">Parcela</span><span className="font-semibold text-green-600 truncate">{fmtMoedaSim(ct.valor_parcela)}</span></div>
                <div className="flex items-baseline gap-1 min-w-0"><span className="text-slate-400 shrink-0">Saldo</span><span className="font-semibold text-orange-600 truncate">{fmtMoedaSim(ct.saldo_devedor)}</span></div>
                <div className="flex items-baseline gap-1 min-w-0"><span className="text-slate-400 shrink-0">Pagas/Rest.</span><span className="font-medium truncate">{(ct.parcela_atual || 0)}/{(ct.parcelas_restantes || 0)}</span></div>
                <div className="flex items-baseline gap-1 min-w-0"><span className="text-slate-400 shrink-0">Início</span><span className="font-medium truncate">{fmtDataSim(ct.data_inicio)}</span></div>
                <div className="flex items-baseline gap-1 min-w-0"><span className="text-slate-400 shrink-0">Fim</span><span className="font-medium truncate">{fmtDataSim(ct.data_fim)}</span></div>
              </div>
              <button
                type="button"
                onClick={() => onExcluirContrato && onExcluirContrato(ct.id, ct.tipo)}
                data-testid={`sim-excluir-contrato-${ct.id}`}
                className="shrink-0 text-red-500 hover:text-red-700 p-1.5 rounded hover:bg-red-50 transition-colors"
                title="Excluir contrato"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      {simulacao.ativo && (
        <div className="space-y-2">
          <div className="grid grid-cols-9 gap-2 items-end">
            <div>
              <Label className="text-[10px] text-slate-500">Banco*</Label>
              <Select 
                value={simulacao.banco} 
                onValueChange={(v) => {
                  updateSimulacao(tipo, 'banco', v);
                  updateSimulacao(tipo, 'tabela', '');
                  updateSimulacao(tipo, 'tabelaId', '');
                  updateSimulacao(tipo, 'correspondente', '');
                  updateSimulacao(tipo, 'produto', '');
                }}
              >
                <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Banco" /></SelectTrigger>
                <SelectContent>
                  {getBancosParaSimulacao(tipoOperacaoKey).length > 0 ? (
                    getBancosParaSimulacao(tipoOperacaoKey)
                      .filter(b => b && b.id)
                      .map((b, i) => <SelectItem key={`sim-banco-${b.id}-${i}`} value={b.id}>{b.nome}</SelectItem>)
                  ) : (
                    bancos
                      .filter(b => b && b.id)
                      .map((b, i) => <SelectItem key={`sim-banco-all-${b.id}-${i}`} value={b.id}>{b.nome}</SelectItem>)
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Instituição</Label>
              <Input className="h-7 text-xs bg-slate-50" value={instituicaoSelecionada || "-"} disabled />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Tipo Op.</Label>
              <Input className={cn("h-7 text-xs font-medium", corAtual.tipoOp)} value={tipoOperacaoDisplay} disabled />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Correspondente</Label>
              <Select value={simulacao.correspondente} onValueChange={(v) => updateSimulacao(tipo, 'correspondente', v)}>
                <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Corresp." /></SelectTrigger>
                <SelectContent>
                  {(simulacao.banco ? getCorrespondentesDoBanco(simulacao.banco) : correspondentes)
                    .filter(c => c && c.id)
                    .map((c, i) => (
                    <SelectItem key={`sim-corresp-${c.id}-${i}`} value={c.id}>{c.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Tabela</Label>
              <Select 
                value={simulacao.tabelaId} 
                onValueChange={(v) => {
                  const tabela = tabelasComissao.find(t => t.id === v);
                  updateSimulacao(tipo, 'tabelaId', v);
                  updateSimulacao(tipo, 'tabela', tabela?.nome || '');
                  updateSimulacao(tipo, 'produto', tabela?.produto || '');
                }}
              >
                <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Tabela" /></SelectTrigger>
                <SelectContent>
                  {(simulacao.banco ? getTabelasParaBanco(simulacao.banco) : tabelasComissao)
                    .filter(t => t && t.id)
                    .map((t, i) => (
                    <SelectItem key={`sim-tab-${t.id}-${i}`} value={t.id}>{t.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Produto</Label>
              <Input className="h-7 text-xs bg-slate-50" placeholder="Auto" value={simulacao.produto} disabled />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Vl. Parc.</Label>
              <Input 
                className="h-7 text-xs" 
                placeholder="0,00" 
                value={simulacao.valorParc} 
                onChange={(e) => updateSimulacao(tipo, 'valorParc', formatValorBR(e.target.value))} 
                onBlur={(e) => updateSimulacao(tipo, 'valorParc', formatValorBROnBlur(e.target.value))}
              />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Vl. Líquido</Label>
              <Input 
                className={cn("h-7 text-xs", corAtual.valorLiq)}
                placeholder="0,00" 
                value={simulacao.valorLiquido} 
                onChange={(e) => updateSimulacao(tipo, 'valorLiquido', formatValorBR(e.target.value))} 
                onBlur={(e) => updateSimulacao(tipo, 'valorLiquido', formatValorBROnBlur(e.target.value))}
              />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Parcelas</Label>
              <Input className="h-7 text-xs w-16" type="number" value={simulacao.qtdeParcela} onChange={(e) => updateSimulacao(tipo, 'qtdeParcela', e.target.value)} />
            </div>
          </div>
          {/* Parâmetros e critérios */}
          <div className="bg-slate-50 rounded p-2 mt-2">
            {simulacao.banco ? (
              (() => {
                const analise = analisarCriteriosSimulacao(tipo);
                if (!analise) {
                  return <p className="text-[10px] text-slate-400">Nenhum parâmetro cadastrado para este banco/instituição</p>;
                }
                return (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${analise.statusGeral === "APROVADO" ? "bg-green-500 text-white" : "bg-red-500 text-white"}`}>
                        {analise.statusGeral === "APROVADO" ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                        {analise.statusGeral} {analise.aprovados}/{analise.totalCriterios}
                      </span>
                      {analise.criterios.map((c, idx) => (
                        <span key={idx} className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] border ${c.aprovado ? "border-green-400 bg-green-50 text-green-700" : "border-red-400 bg-red-50 text-red-700"}`}>
                          {c.aprovado ? <CheckCircle2 className="h-2.5 w-2.5" /> : <XCircle className="h-2.5 w-2.5" />}
                          {c.nome} | P:{c.valorParametro} | C:{c.valorContrato}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()
            ) : (
              <p className="text-[10px] text-slate-400">Selecione um banco para ver parâmetros e critérios</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Componente para cards simples (Margem Aumento, IR, Seguro)
const SimulacaoCardSimples = ({
  tipo,
  titulo,
  icon: Icon,
  cor,
  simulacao,
  updateSimulacao,
  toggleSimulacao,
  bancos,
  formatValorBR,
  formatValorBROnBlur,
  camposExtras = null
}) => {
  const cores = {
    blue: { border: "border-blue-500 bg-blue-50/30", icon: "text-blue-600" },
    purple: { border: "border-purple-500 bg-purple-50/30", icon: "text-purple-600" },
    red: { border: "border-red-500 bg-red-50/30", icon: "text-red-600" }
  };
  const corAtual = cores[cor] || cores.blue;

  return (
    <div className={cn(
      "border rounded-lg p-3 transition-all",
      simulacao.ativo ? corAtual.border : "border-slate-200"
    )}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className={cn("h-4 w-4", corAtual.icon)} />
          <span className="font-semibold text-xs">{titulo}</span>
        </div>
        <Switch checked={simulacao.ativo} onCheckedChange={() => toggleSimulacao(tipo)} />
      </div>
      <div className="flex items-center gap-1 mb-2">
        <span className="text-[10px] text-muted-foreground">Margem:</span>
        <Input className="h-5 w-16 text-[10px] px-1" placeholder="0,00" value={simulacao.margemDisponivel} 
          onChange={(e) => updateSimulacao(tipo, 'margemDisponivel', formatValorBR(e.target.value))}
          onBlur={(e) => updateSimulacao(tipo, 'margemDisponivel', formatValorBROnBlur(e.target.value))}
        />
      </div>
      {simulacao.ativo && (
        <div className="space-y-1">
          {tipo !== 'seguro' ? (
            <>
              <Select value={simulacao.banco} onValueChange={(v) => updateSimulacao(tipo, 'banco', v)}>
                <SelectTrigger className="h-6 text-[10px]"><SelectValue placeholder="Banco" /></SelectTrigger>
                <SelectContent>{bancos.map(b => <SelectItem key={b.id} value={b.id}>{b.nome}</SelectItem>)}</SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-1">
                <Input className="h-6 text-[10px]" placeholder="Vl. Parc." value={simulacao.valorParc} 
                  onChange={(e) => updateSimulacao(tipo, 'valorParc', formatValorBR(e.target.value))} 
                  onBlur={(e) => updateSimulacao(tipo, 'valorParc', formatValorBROnBlur(e.target.value))}
                />
                <Input className="h-6 text-[10px]" type="number" placeholder="Parcelas" value={simulacao.qtdeParcela} 
                  onChange={(e) => updateSimulacao(tipo, 'qtdeParcela', e.target.value)} 
                />
              </div>
            </>
          ) : (
            <>
              <Input className="h-6 text-[10px]" placeholder="Seguradora" value={simulacao.seguradora} 
                onChange={(e) => updateSimulacao(tipo, 'seguradora', e.target.value)} 
              />
              <div className="grid grid-cols-2 gap-1">
                <Input className="h-6 text-[10px]" placeholder="Vl. Parc." value={simulacao.valorParc} 
                  onChange={(e) => updateSimulacao(tipo, 'valorParc', formatValorBR(e.target.value))} 
                  onBlur={(e) => updateSimulacao(tipo, 'valorParc', formatValorBROnBlur(e.target.value))}
                />
                <Input className="h-6 text-[10px]" placeholder="Taxa %" value={simulacao.taxaJuros} 
                  onChange={(e) => updateSimulacao(tipo, 'taxaJuros', e.target.value)} 
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Componente principal da seção de simulação
export default function SimulacaoSection({
  simulacoes,
  contratos = [],
  updateSimulacao,
  toggleSimulacao,
  bancos,
  correspondentes,
  tabelasComissao,
  instituicaoSelecionada,
  getBancosParaSimulacao,
  getTabelasParaBanco,
  getCorrespondentesDoBanco,
  analisarCriteriosSimulacao,
  formatValorBR,
  formatValorBROnBlur,
  onExcluirContrato
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Calculator className="h-5 w-5 text-blue-600" />
          Simulação
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Margem Livre */}
        <SimulacaoCard
          tipo="margemLivre"
          titulo="Margem Livre"
          icon={DollarSign}
          cor="green"
          simulacao={simulacoes.margemLivre}
          updateSimulacao={updateSimulacao}
          toggleSimulacao={toggleSimulacao}
          bancos={bancos}
          correspondentes={correspondentes}
          tabelasComissao={tabelasComissao}
          instituicaoSelecionada={instituicaoSelecionada}
          getBancosParaSimulacao={getBancosParaSimulacao}
          getTabelasParaBanco={getTabelasParaBanco}
          getCorrespondentesDoBanco={getCorrespondentesDoBanco}
          analisarCriteriosSimulacao={analisarCriteriosSimulacao}
          formatValorBR={formatValorBR}
          formatValorBROnBlur={formatValorBROnBlur}
          tipoOperacaoDisplay="MARGEM NOVA"
          tipoOperacaoKey="MARGEM_NOVA"
        />

        {/* Cartão RMC */}
        <SimulacaoCard
          tipo="cartaoRMC"
          titulo="CARTÃO CONSIGNADO RMC"
          icon={CreditCard}
          cor="orange"
          simulacao={simulacoes.cartaoRMC}
          updateSimulacao={updateSimulacao}
          toggleSimulacao={toggleSimulacao}
          bancos={bancos}
          correspondentes={correspondentes}
          tabelasComissao={tabelasComissao}
          instituicaoSelecionada={instituicaoSelecionada}
          getBancosParaSimulacao={getBancosParaSimulacao}
          getTabelasParaBanco={getTabelasParaBanco}
          getCorrespondentesDoBanco={getCorrespondentesDoBanco}
          analisarCriteriosSimulacao={analisarCriteriosSimulacao}
          formatValorBR={formatValorBR}
          formatValorBROnBlur={formatValorBROnBlur}
          tipoOperacaoDisplay="CARTÃO RMC"
          tipoOperacaoKey="CARTAO_RMC"
          contratosVinculados={(contratos || []).filter(c => String(c.tipo_operacao || "").toUpperCase() === "CARTÃO CONSIGNADO")}
          onExcluirContrato={onExcluirContrato}
        />

        {/* Cartão Benefício */}
        <SimulacaoCard
          tipo="cartaoBeneficio"
          titulo="Cartão Benefício"
          icon={CreditCard}
          cor="cyan"
          simulacao={simulacoes.cartaoBeneficio}
          updateSimulacao={updateSimulacao}
          toggleSimulacao={toggleSimulacao}
          bancos={bancos}
          correspondentes={correspondentes}
          tabelasComissao={tabelasComissao}
          instituicaoSelecionada={instituicaoSelecionada}
          getBancosParaSimulacao={getBancosParaSimulacao}
          getTabelasParaBanco={getTabelasParaBanco}
          getCorrespondentesDoBanco={getCorrespondentesDoBanco}
          analisarCriteriosSimulacao={analisarCriteriosSimulacao}
          formatValorBR={formatValorBR}
          formatValorBROnBlur={formatValorBROnBlur}
          tipoOperacaoDisplay="CARTÃO BENEF."
          tipoOperacaoKey="CARTAO_BENEFICIO"
          contratosVinculados={(contratos || []).filter(c => String(c.tipo_operacao || "").toUpperCase() === "CARTÃO BENEFÍCIO")}
          onExcluirContrato={onExcluirContrato}
        />

        {/* Cards simples em grid */}
        <div className="grid grid-cols-3 gap-3 mt-4">
          <SimulacaoCardSimples
            tipo="margemAumento"
            titulo="Margem Aumento"
            icon={TrendingUp}
            cor="blue"
            simulacao={simulacoes.margemAumento}
            updateSimulacao={updateSimulacao}
            toggleSimulacao={toggleSimulacao}
            bancos={bancos}
            formatValorBR={formatValorBR}
            formatValorBROnBlur={formatValorBROnBlur}
          />
          <SimulacaoCardSimples
            tipo="margemIR"
            titulo="Margem IR"
            icon={Percent}
            cor="purple"
            simulacao={simulacoes.margemIR}
            updateSimulacao={updateSimulacao}
            toggleSimulacao={toggleSimulacao}
            bancos={bancos}
            formatValorBR={formatValorBR}
            formatValorBROnBlur={formatValorBROnBlur}
          />
          <SimulacaoCardSimples
            tipo="seguro"
            titulo="Seguro"
            icon={ShieldCheck}
            cor="red"
            simulacao={simulacoes.seguro}
            updateSimulacao={updateSimulacao}
            toggleSimulacao={toggleSimulacao}
            bancos={bancos}
            formatValorBR={formatValorBR}
            formatValorBROnBlur={formatValorBROnBlur}
          />
        </div>
      </CardContent>
    </Card>
  );
}
